<template>
  <div>
    <!-- adding indicator to single theme alert -->
    <div
      v-if="this.addingIndicatorToTheme"
      class="position-relative alert-success"
    >
      <div class="alert-content">
        <div class="d-flex justify-start align-center flex-grow-1">
          <v-icon icon="mdi-checkbox-marked-circle"></v-icon>
          <div class="text-left pl-2">
            <p class="font-weight-medium">
              You're adding indicators to the theme "{{
                selectedThemeName.trim()
              }}"
            </p>
            <p>
              To add indicators to a different theme or multiple themes, you can
              remove this setting
            </p>
          </div>
        </div>
        <div class="d-flex justify-end flex-shrink-1">
          <v-btn variant="text" @click="resetIndicatorTheme()"
            >Remove Setting</v-btn
          >
        </div>
      </div>
    </div>

    <!-- indicator search content -->
    <v-card-text class="text-left pa-0 d-flex justify-center">
      <v-card-text class="pa-0" style="max-width: 1024px">
        <!-- Search box -->
        <div class="pa-0 my-4">
          <IndicatorSearch
            v-model:filters="filters"
            @search="debouncedSearch"
            data-testid="search-box"
          />
        </div>
        <v-card class="my-2" elevation="0" style="background-color: #f9f9f9">
          <!-- Tags -->
          <IndicatorSearchTags
            v-model:tagsProp="filters.tags"
            @search="debouncedSearch"
            :filterBlackList="['coverage']"
          />

          <v-card-text class="py-0 px-2">
            <!-- Filters -->
            <v-row
              v-if="indicators?.length || fetchingIndicators"
              class="px-0 my-2 align-center"
            >
              <v-col cols="9" class="text-h6 text pb-0">
                {{
                  fetchingIndicators
                    ? "Searching..."
                    : totalResultsCount === 1
                      ? `We found 1 result that matches your query`
                      : `We found ${totalResultsCount} results that match your query`
                }}
              </v-col>
              <v-col cols="3 pb-0">
                <!-- Wrap search call in inline function to prevent passing asc/desc as a param -->
                <v-select
                  v-model="filters.sortBy"
                  :items="[
                    { title: 'Ascending (A-Z)', value: 'asc' },
                    { title: 'Descending (Z-A)', value: 'desc' },
                  ]"
                  no-data-text="No filters available"
                  hide-details
                  rounded="0"
                  style="background-color: white"
                  @update:modelValue="() => search()"
                  label="Sort"
                  variant="outlined"
                  density="compact"
                  data-testid="sort-dropdown"
                >
                  <template v-slot:selection="{ item }">
                    <span data-testid="sort-direction-text">{{
                      item.title
                    }}</span>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Suggested prompts -->
            <v-card-text
              v-show="
                (!indicators?.length || indicators.length == 0) &&
                !noResults &&
                !fetchingIndicators
              "
              class="pa-0"
            >
              <IndicatorSearchSuggestions
                v-model:filtersProp="filters"
                @search="debouncedSearch"
                getSuggestionsAPI="themes-indicator-search-suggestions"
              />
            </v-card-text>

            <!-- Fetched indicators -->
            <v-card-text v-if="indicators?.length" class="pa-0">
              <IndicatorCard
                v-for="indicator in indicators"
                :key="indicator.id"
                :indicator="indicator"
                :opened="false"
                :showLegend="false"
              >
                <template #headerBtn>
                  <v-btn
                    color="success"
                    prepend-icon="mdi-plus"
                    @click.stop="addIndicatorToOneOrManyThemes(indicator)"
                    variant="elevated"
                    rounded="0"
                    class="mr-2"
                    :disabled="disableAddIndicator[indicator.id] ?? false"
                    data-testid="add-to-theme-button"
                  >
                    add to theme
                  </v-btn>
                </template>
                <template #firstColumn>
                  <!-- Extra buttons -->
                  <div class="d-flex pt-2">
                    <v-spacer />
                    <v-btn
                      color="success"
                      prepend-icon="mdi-plus"
                      @click="addIndicatorToOneOrManyThemes(indicator)"
                      variant="elevated"
                      rounded="0"
                      class="mr-2"
                      :disabled="disableAddIndicator[indicator.id] ?? false"
                    >
                      add to theme
                    </v-btn>
                  </div>
                </template>
              </IndicatorCard>
            </v-card-text>

            <!-- Skeleton laoders for indicator cards. We can see them while searching and then we chuck them underneath for lazy loading -->
            <v-card-text
              v-if="
                fetchingIndicators ||
                (indicators?.length && !areAllIndicatorsFetched)
              "
              class="pa-0 paginatedSkeletons"
            >
              <IndicatorCard
                v-for="n in 5"
                :key="n"
                :loading="true"
                :opened="false"
              >
                <!-- Extra buttons loading -->
                <template #firstColumn>
                  <div class="d-flex pt-2">
                    <v-spacer />
                    <v-btn
                      disabled
                      color="success"
                      prepend-icon="mdi-plus"
                      variant="elevated"
                      rounded="0"
                      class="mr-2"
                    >
                      add to theme
                    </v-btn>
                  </div>
                </template>
              </IndicatorCard>
            </v-card-text>

            <!-- No results found -->
            <v-card-text v-if="noResults" class="pa-0 pt-2">
              <v-alert
                color="warning"
                icon="mdi-information"
                size="10px"
                prominent
                class="datastoreNoResults"
              >
                <div style="font-weight: 500">No search results found</div>
                <div>
                  We were unable to find any search results for your query,
                  please try again with a different parameters
                </div>
              </v-alert>
            </v-card-text>
          </v-card-text>
        </v-card>
      </v-card-text>

      <!-- Add indicator to themes -->
      <ThemesManagerSearchAddIndicator
        v-model:addIndicatorDialog="addIndicatorDialog"
        :selectedIndicator="selectedIndicator"
        @reloadThemesManager="() => this.$emit('reloadThemesManager')"
      />
    </v-card-text>

    <!-- dialogs -->

    <!-- create a theme dialog -->
    <DynamicDialog
      :show="showCreateThemeDialog"
      @dialogOk="
        showCreateThemeDialog = false;
        this.$router.push({ path: '/themes/manage' });
      "
      @dialogCancel="showCreateThemeDialog = false"
      @update:showDyamicDialog="(state) => (showCreateThemeDialog = state)"
      max-width="500"
      okBtnColor="info"
      :cancelBtnHidden="true"
      data-testid="create-theme-dialog"
    >
      <template v-slot:title>Build Your First Theme</template>
      <template v-slot:content>
        <p class="pb-4">
          You need at least one theme to add this indicator. Create a theme to
          organise your data and unlock deeper insights
        </p>
      </template>
      <template v-slot:okBtnTitle>Create a Theme</template>
    </DynamicDialog>
  </div>
</template>
<script>
import IndicatorCard from "./IndicatorCard.vue";
import IndicatorSearchTags from "@/components/IndicatorSearchTags.vue";
import IndicatorSearch from "@/components/IndicatorSearch.vue";
import ThemesManagerSearchAddIndicator from "@/components/ThemesManagerSearchAddIndicator.vue";
import IndicatorSearchSuggestions from "@/components/IndicatorSearchSuggestions.vue";
import DynamicDialog from "@/components/DynamicDialog";
import { systemMessages } from "@/mixins/SystemMessages";
import { useDisplay } from "vuetify";
import { Debounce } from "@/mixins/Debounce";

export default {
  name: "ThemesManagerSearch",
  components: {
    IndicatorCard,
    IndicatorSearchTags,
    IndicatorSearch,
    ThemesManagerSearchAddIndicator,
    IndicatorSearchSuggestions,
    DynamicDialog,
  },
  mixins: [systemMessages, Debounce],
  data: () => ({
    suggestedPrompts: [],
    pageHeight: useDisplay().height,
    indicators: [],
    filters: {
      searchString: "",
      tags: [],
      sortBy: "asc",
    },
    fetchingIndicators: false,
    areAllIndicatorsFetched: false,
    noResults: false,
    paginatedFetch: false,
    totalResultsCount: 0,
    addIndicatorDialog: false,
    selectedIndicator: null,
    indicatorThemes: {},
    abortController: null,
    debouncedSearch: null,
    disableAddIndicator: {},
    showCreateThemeDialog: false,
  }),
  computed: {
    selectedThemeName: {
      get() {
        // Get the selected theme name from the Vuex store
        return this.$store.getters["themesManager/selectedThemeName"];
      },
      set(val) {
        // Update the selected theme name in the Vuex store
        this.$store.commit("themesManager/setSelectedThemeName", val);
      },
    },
    selectedThemeId: {
      get() {
        return this.$store.getters["themesManager/selectedThemeId"];
      },
      set(val) {
        this.$store.commit("themesManager/setSelectedThemeId", val);
      },
    },
    addingIndicatorToTheme: {
      get() {
        return this.$store.getters["themesManager/addingIndicatorToTheme"];
      },
      set(val) {
        this.$store.commit("themesManager/setAddingIndicatorToTheme", val);
      },
    },
    themes: {
      get() {
        return this.$store.getters["themesManager/themes"];
      },
    },
  },
  mounted() {
    this.filters.searchString = this.$route.query?.s || "";
    this.filters.sortBy = this.$route.query?.sort || "asc";
    this.filters.tags = JSON.parse(this.$route.query?.tags || "[]");

    // if anything is coming through the URL - run search
    if (this.filters.searchString || this.filters.tags?.length) {
      this.search();
    }

    /** seems overengineered - but required
     * it is possible in some circumstances to fire search multiple times in quick succession
     * resulting in duplicated results. An example is a short result list such as custom data
     * while scrolled to the bottom and another search to check for paginated results is fired
     * immediately.  A debounce swallows any rapid suplicate firings of query.
     **/
    this.debouncedSearch = this.debounce(this.search, 200);
  },
  methods: {
    async search(offset = 0) {
      /**
       * Seems over engineered but required - if an AJAX search request is 'pending'
       * and another search request is fired then cancel the previous request and submit the
       * new one.  An edge case, but if a tag is added and the query runs long enough for the
       * user to remove the tag before the results are returned then the initial query/wrong
       * outcome can be displayed.  Using the abort controller is a nice way to invalidate backend
       * requests and keep the frontend in sync.
       **/
      if (this.abortController) {
        this.abortController.abort("AbortError");
        this.fetchingIndicators = false;
      }

      //only search if there is a search string and/or filters set
      if (this.filters["searchString"] || this.filters.tags?.length) {
        if (!this.paginatedFetch) {
          this.indicators = [];
        }

        this.fetchingIndicators = true;
        this.noResults = false;

        this.filters.offset = offset;
        this.filters.limit = this.getAvailableSlotsForIndicators();

        try {
          //set up new abort signal for this request so it can be cancelled
          this.abortController = new AbortController();
          const signal = this.abortController.signal;

          const response = await this.$axios.post(
            "themes-manager-indicator-search",
            this.filters,
            { signal },
          );

          //set indicators and totals details
          if (!this.paginatedFetch) {
            //rebuild indicators array and assign it to maintain reactivity (rather than push)
            let updatedIndicators = this.$cloneDeep(this.indicators);
            updatedIndicators = updatedIndicators.concat(
              response.data.indicators,
            );
            this.indicators = updatedIndicators;
            this.areAllIndicatorsFetched =
              response.data.areAllIndicatorsFetched;
            this.totalResultsCount = response.data.totalCount;
            this.noResults = !this.indicators?.length;
          }

          this.fetchingIndicators = false;

          //return a response so that paginated call knows search has finished
          return response;
        } catch (error) {
          if (error.code == "ERR_CANCELED") {
            //console.log("Request canceled");
          } else {
            this.errorMsg({
              title: "There was an error searching indicators",
              message: error.response?.data?.message,
            });
          }
        }
      } else {
        this.noResults = false;
        this.indicators = [];
      }
    },
    async getPaginatedIndicators() {
      // do nothing if there are no more indicators to fetch || a search is in progress || there are no search params
      if (
        this.areAllIndicatorsFetched ||
        this.paginatedFetch ||
        (!this.filters["searchString"] && !this.filters.tags?.length)
      ) {
        return;
      }

      this.paginatedFetch = true;

      const offset = this.indicators?.length ?? 0;

      //trigger an indicator search with offset from current results
      try {
        const response = await this.debouncedSearch(offset);
        //rebuild indicators array and assign it to maintain reactivity (rather than push)
        let updatedIndicators = this.$cloneDeep(this.indicators);
        updatedIndicators = updatedIndicators.concat(response.data.indicators);
        this.indicators = updatedIndicators;
        this.areAllIndicatorsFetched = response.data.areAllIndicatorsFetched;
      } catch (error) {
        console.error(error);
      } finally {
        this.paginatedFetch = false;
      }
    },
    // Fetch indicators based on the user's display height
    getAvailableSlotsForIndicators() {
      const INDICATOR_CARD = 94;
      const freeSpace = this.pageHeight - 300;

      return Math.floor(freeSpace / INDICATOR_CARD); // plus extra
    },
    // Handles the "add to theme" button on each indicator card and tracking if a theme is being added to
    async addIndicatorToOneOrManyThemes(indicator) {
      //show dialog to create a theme if there are no themes
      if (!this.themes?.length) {
        this.showCreateThemeDialog = true;
        return;
      }

      //add indicator to single or multiple themes
      this.selectedIndicator = indicator;

      // user has navigated directly to indicator search - show dialog to choose theme
      if (!this.addingIndicatorToTheme) {
        this.addIndicatorDialog = true;
      } else {
        //user has been redirected to indicator search from 'add indicator' event
        const data = {
          themeIds: [this.selectedThemeId],
          indicator_code: indicator.indicator_code,
        };

        try {
          this.warningPill({
            icon: "mdi-plus",
            title: `Adding indicator to ${this.selectedThemeName}`,
          });

          //update single theme
          await this.$axios.post("/themes-manager-indicators", data);

          this.indicatorThemes[indicator.id] = [
            ...(this.indicatorThemes[indicator.id] || []),
            this.selectedThemeId,
          ];

          //update button disabled state
          this.setAddIndicatorDisabledState(indicator);

          this.successPill({
            icon: "mdi-plus",
            title: `Successfully added indicator to ${this.selectedThemeName}`,
          });
          this.$emit("reloadThemesManager");
        } catch (error) {
          this.errorMsg({
            title: `There was an error adding the indicator ${this.selectedThemeName}`,
            message: error.response?.data?.message,
          });
        }
      }
    },
    // Goes through each indicator and checks if it is already assigned to the selected theme, then disables the
    // "add to theme" button accordingly.
    async setSingleThemeindicatorButtonState() {
      /**
       * if we're adding indicators to a single theme (via add indicator)
       * update the disabled add indicator button state for each of the
       * paginated indicators
       */
      if (this.addingIndicatorToTheme && this.selectedThemeId) {
        for (const indicator of this.indicators) {
          if (!indicator.theme_ids || indicator.theme_ids.length === 0) {
            continue;
          }
          this.indicatorThemes[indicator.id] = indicator.theme_ids || [];

          //fire method that sets button disabled state for the indicator
          this.setAddIndicatorDisabledState(indicator);
        }
      }
    },
    //fetch array of themes from backend that use the supplied indicator
    async getIndicatorThemes(indicatorCode) {
      try {
        const response = await this.$axios.get(
          `/themes-indicator-flag/${indicatorCode}`,
        );
        return response.data;
      } catch (error) {
        this.errorMsg({
          title: "There was a problem fetching indicator theme details",
          message: error.response?.data?.message,
        });
      }
    },
    //add indicator button disabled state for given indicator
    setAddIndicatorDisabledState(indicator) {
      //if we're adding indicator to multiple themes (dialog) then the button is always enabled
      if (!this.selectedThemeId) {
        this.disableAddIndicator = {};
        return;
      }

      /**
       * adding indicator to single theme
       * if the selected theme already has indicator assigned to it
       * then the indicator button should be disabled.  Otherwise the
       * button is enabled.
       */
      if (
        Object.prototype.hasOwnProperty.call(
          this.indicatorThemes,
          indicator.id,
        ) &&
        this.indicatorThemes[indicator.id].includes(this.selectedThemeId)
      ) {
        this.disableAddIndicator[indicator.id] = true;
      } else {
        this.disableAddIndicator[indicator.id] = false;
      }
    },
    //reset indicator search to apply to multiple themes rather than single theme
    resetIndicatorTheme() {
      this.addingIndicatorToTheme = false;
      this.selectedThemeId = null;
      this.selectedThemeName = "";
      this.disableAddIndicator = {};
      this.indicatorThemes = {};
    },
  },
  watch: {
    filters: {
      handler(val) {
        //set querystring params when search/filter tags change
        this.$router.push({
          path: "/themes/search",
          query: {
            s: val.searchString,
            sort: val.sortBy,
            tags: JSON.stringify(val.tags),
          },
        });
      },
      deep: true,
    },
    indicators: {
      handler() {
        if (this.selectedThemeId && this.indicators.length > 0) {
          this.setSingleThemeindicatorButtonState();
        }
      },
    },
    selectedThemeId: {
      handler() {
        //if the theme id has changed to null (e.g. no themes) reset the addIndicatorToTheme flags
        if (!this.selectedThemeId) {
          this.resetIndicatorTheme();
        }
      },
    },
    addingIndicatorToTheme: {
      handler(val) {
        if (val) {
          this.setSingleThemeindicatorButtonState();
        }
      },
    },
  },
};
</script>
<style scoped>
.alert-success {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #c6ecc6;
  color: #1a3e1c;
  width: 100vw;
  min-height: 70px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.alert-success .alert-content {
  display: flex;
  width: 1024px;
  justify-content: space-between;
  align-items: center;
}
</style>
